.container {
  display: flex;
  flex-direction: column;
  height: 100%;
  /* Auto-scales between popup (360px) and sidepanel (380px)
   * containers — rows use flex with `justify-content: space-between`
   * so the controls push to the right edge regardless of container
   * width. */
  width: 100%;
}

/* Header — chevron back button + title. Per the refreshed Figma
 * spec (node 1108:2605): 12px vertical / 16px horizontal padding,
 * 2px gap between the IconButton and the title, bottom border
 * divider. */
.header {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-12) var(--spacing-16);
  border-bottom: 1px solid var(--stroke-neutral);
}

/* Reset the default margin <Text as="h2"> inherits from UA styles. */
.title {
  margin: 0;
}

.content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  /* Top padding 12px per Figma (node 1108:2608). No horizontal
   * padding on the wrapper — each row owns its own 16px inset so
   * section dividers can span edge-to-edge. */
  padding-top: var(--spacing-12);
}

.section {
  display: flex;
  flex-direction: column;
}

/* Section headers (General, Notifications, Privacy & security)
 * — tertiary text, semibold body, 16px horizontal inset, 12px
 * vertical padding per Figma. The <h3> carries margin:0 to
 * override UA heading defaults.
 *
 * S7 rhythm pass: quieter headers — uppercase + tracked-out instead
 * of the previous body-sized heading, so the group label reads as
 * ambient structure rather than another line of content. */
.sectionHeader {
  margin: 0;
  padding: var(--spacing-12) var(--spacing-16);
  text-transform: uppercase;
  /* Uppercase text reads cramped at this size without extra tracking —
   * 0.06em opens it back out enough to stay legible as a quiet label. */
  letter-spacing: 0.06em;
}

/* DND row label — sits above <DndEditor> inside the Notifications
 * section. Same 12/16 inset rhythm as `.sectionHeader`, but plain
 * weight/case — it's a row label for one control cluster (the FIX 2
 * settings-declutter restore, spec §4.3), not a new group, so it
 * skips the uppercase/tracked SectionHeader treatment above. */
.dndTitleRow {
  padding: var(--spacing-12) var(--spacing-16) var(--spacing-4);
}

/* Flat-list row — 48px min-height, 16px horizontal inset, 6px
 * vertical padding so the 28px TabGroup tabs / 20px lg Toggle
 * tracks center-align inside. `min-height` (not `height`) so a
 * wrapped label in a narrower locale expands the row instead of
 * clipping. justify-content: space-between pushes label left and
 * control right regardless of container width. */
/* 6px literal: tokens-figma.css has no --spacing-6 in its 2/4/8/12/16
 * scale (matches the Figma spacing token set). The Figma row spec
 * pins vertical padding to 6px specifically so the 28px TabGroup
 * tabs and 20px lg Toggle tracks center inside the 48px row with a
 * couple of px of flex slack. Using --spacing-4 or --spacing-8
 * would shift the control baseline by 2px. */
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 6px var(--spacing-16);
  gap: var(--spacing-12);
  box-sizing: border-box;
}

/* Keep the label (first child of each row) from wrapping or
 * shrinking against the right-hand control. Carried over from
 * commit 6eb019e — load-bearing for long i18n labels (DE/FR
 * "Anrufbenachrichtigungen") inside the 360px popup. The rule
 * applies structurally to every present and future row
 * regardless of label content. */
.row > :first-child {
  white-space: nowrap;
  flex-shrink: 0;
}

/* `<label>` wrapping the notification-toggle Text. Cursor hints
 * the label is an interactive click-target for the sibling
 * Toggle (associated via htmlFor / id). */
.rowLabel {
  cursor: pointer;
}

/* Section divider — neutral strip between sections. Not a border
 * because the Figma design shows breathing room, not a hairline.
 * Full-width so it spans the container edge-to-edge.
 *
 * S7 rhythm pass: the divider carries no background-color of its own
 * (it's whitespace, not a painted stroke) and `tokens-figma.css` ships
 * a single `--stroke-neutral` per theme with no lighter alias to step
 * to, so a color-based "lighten" has nothing to act on yet (opacity
 * against a colorless background is a no-op — don't reach for it).
 * Stepping the height down one spacing tier (8px -> 4px) is the lever
 * that actually does something today; revisit with a real
 * lighter-stroke token later. */
.sectionDivider {
  height: var(--spacing-4);
  flex-shrink: 0;
}

/* 10px literal: tokens-figma.css has no --spacing-10 in its
 * 2/4/8/12/16 scale. The confirm row needs a gap between the
 * "Are you sure?" copy and the Yes/Cancel button pair that sits
 * between --spacing-8 (too tight against the 32px button height)
 * and --spacing-12 (reads as a paragraph break). Kept as a
 * literal until design ships a --spacing-10 token. */
.confirmRow {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.confirmActions {
  display: flex;
  gap: var(--spacing-8);
}

/* Explicit flex:1 on both confirm buttons (carried over from the
 * previous layout) so the Yes/Cancel pair splits the confirm row
 * evenly. `width: 100%` on two flex children relies on default
 * flex-shrink to resolve — flex:1:1:0 is the structural
 * guarantee. */
.confirmAction {
  flex: 1;
}

/* Support row — inline `<Link>` with surrounding copy. Always
 * rendered as a child of `.footer` now, which already supplies the
 * horizontal inset and the between-children gap — no padding of its
 * own here, or the two would stack into a double indent. */
.supportRow {
  padding: 0;
}

/* Caption under a toggle row (currently the Privacy section's
 * analytics toggle) — same 16px horizontal inset as `.row`, with
 * 12px of breathing room below before the next section divider. */
.rowDescription {
  margin: 0;
  padding: 0 var(--spacing-16) var(--spacing-12);
}

/* Language picker — native select styled with the Input primitive's
 * token vocabulary. max-width guards the 360px popup against long
 * endonyms ("Português (Brasil)"); the row's space-between handles
 * the rest. */
.languageSelect {
  font: var(--text-body-medium);
  color: var(--text-primary);
  background: var(--bg-primary);
  border: 1px solid var(--stroke-neutral);
  border-radius: 8px;
  padding: 4px 8px;
  max-width: 200px;
}

/* Collapsed-disclosure row (`ExtraSectionBody`'s `collapsed` branch)
 * — same 12/16px inset as `.row` but a real `<button>` so it's
 * natively focusable/keyboard-activatable; background/border reset
 * to keep it visually identical to a plain row until interacted
 * with. */
.disclosureRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--spacing-12) var(--spacing-16);
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
}

/* Chevron glyph for `.disclosureRow`. `--icon-muted` matches how
 * every other icon in this file gets its quiet/secondary color (see
 * `Icon.module.css`'s `.tone-muted`) — these are raw
 * `@untitledui/icons` elements (not wrapped in the `Icon` primitive),
 * so the token is applied directly via `color` (their SVGs stroke
 * with `currentColor`). */
.chevron,
.chevronOpen {
  width: 16px;
  height: 16px;
  color: var(--icon-muted);
  transition: transform 120ms ease;
}

.chevronOpen {
  transform: rotate(180deg);
}

/* Footer — replaces the old Connection / Send-feedback / About
 * sections. A single quiet column: device status + disconnect,
 * confirm row, version caption, support line, feedback link. Each
 * child leans on this 4px gap for rhythm rather than owning its own
 * vertical padding (see `.supportRow` above). */
.footer {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  padding: var(--spacing-12) var(--spacing-16) var(--spacing-24);
}

.deviceRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-12);
}

.deviceStatus {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
}

.footerCaption {
  margin: 0;
}

/* Reads as a link, not as another caption — it sits directly under the
 * version/support captions and was otherwise pixel-identical to them,
 * giving an actionable control no affordance. Color comes from the
 * child `Text tone="link"`; the hover underline is here because this
 * fires a callback and has no href, so it can't be a `Link`. */
.footerLink {
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  align-self: flex-start;
}

.footerLink:hover {
  text-decoration: underline;
}

/* Host-supplied extra-section content. The HORIZONTAL inset is the panel's
 * job — it owns the settings grid, and a host that forgot it rendered
 * flush against the panel edge (hit three times: the extension's
 * window-mode row, and web's Connectors + safety-number blocks). Vertical
 * rhythm stays with the content, which knows its own spacing needs. */
.extraContent {
  padding-left: var(--spacing-16);
  padding-right: var(--spacing-16);
}
